import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  Flex,
  TEXT_VARIANT_BOLD_LIGHT,
  TEXT_VARIANT_ITALIC_LIGHT,
  Text,
} from 'mailup-web-components'

import type {
  ActivityBounceType,
  ActivityEmailEventType,
} from 'Types/beRecipientsApi'

import type { SearchResult } from 'Pages/RecipientsList/model'

import {
  getBounceText,
  getOptoutText,
} from 'Pages/RecipientsList/recoil/services/recipient'

interface ActivityTitleProps {
  recipient: SearchResult
  activityType: ActivityEmailEventType
  sending: {
    Type?: 'Workflow' | 'Task'
  }
  bounceType: ActivityBounceType
  channel: 'email' | 'sms'
}

const ActivityTitle: FC<ActivityTitleProps> = ({
  activityType,
  sending,
  recipient,
  bounceType,
  channel,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const optoutType =
    channel === 'email' ? recipient.ReasonEmail : recipient.ReasonSms

  const handleBounceRedirect = (): void => {
    navigate(`/recipients/profile/${recipient.Id}/bounce`)
  }

  if (activityType === 'Bounces') {
    return (
      <Flex alignItems="center" gap="4px" flexWrap="wrap">
        <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
          {intl.formatMessage({
            id: 'Load.ActiveRecipientSummary.BounceLabel',
          })}
        </Text>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
          {intl.formatMessage({ id: 'Load.Common.Because' })}
        </Text>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT} textTransform="lowercase">
          {getBounceText(intl, bounceType)}
        </Text>
      </Flex>
    )
  }

  if (activityType === 'Clicks') {
    return (
      <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
        {intl.formatMessage({ id: 'Load.ActiveRecipientSummary.ClickLabel' })}
      </Text>
    )
  }

  if (activityType === 'Opens') {
    return (
      <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
        {intl.formatMessage({
          id: 'Load.ActiveRecipientSummary.UserViewLabel',
        })}
      </Text>
    )
  }

  if (activityType === 'Sendings') {
    return (
      <Flex alignItems="center" gap="4px" flexWrap="wrap">
        <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
          {intl.formatMessage({
            id: 'Load.ActiveRecipientSummary.SendTraceLabel',
          })}
        </Text>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
          {sending !== undefined &&
            sending.Type === 'Workflow' &&
            intl.formatMessage({
              id: 'Load.ActiveRecipientSummary.WithWorkflow',
            })}
          {sending !== undefined &&
            sending.Type === 'Task' &&
            intl.formatMessage({
              id: 'Load.ActiveRecipientSummary.WithTask',
            })}
        </Text>
      </Flex>
    )
  }

  if (activityType === 'Subscription') {
    return (
      <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
        {intl.formatMessage({
          id: 'Load.ActiveRecipientSummary.SubscriptionLabel',
        })}
      </Text>
    )
  }

  if (activityType === 'SubscriptionConfirmation') {
    return (
      <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
        {intl.formatMessage({
          id: 'Load.Common.SubscriptionConfirmation',
        })}
      </Text>
    )
  }

  if (activityType === 'SubscriptionRequest') {
    return (
      <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
        {intl.formatMessage({
          id: 'Load.Common.SubscriptionRequest',
        })}
      </Text>
    )
  }

  if (activityType === 'Unsubscription') {
    return (
      <Flex alignItems="center" gap="4px" flexWrap="wrap">
        <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
          {intl.formatMessage({
            id: 'Load.ActiveRecipientSummary.UnsubscriptionLabel',
          })}
        </Text>
        {optoutType !== undefined && (
          <Text variant={TEXT_VARIANT_ITALIC_LIGHT} textTransform="lowercase">
            {getOptoutText(intl, optoutType)}
          </Text>
        )}
      </Flex>
    )
  }

  return null
}

export default ActivityTitle
